import {
  getTwoFactorStatus,
  listPasskeys,
  useDeletePasskey,
  useGenerateRecoveryCodes,
  useRegisterPasskey,
  useTotpDisable,
  useTotpEnable,
  useTotpSetup,
} from '@/services'
import { TotpSetupResult, TwoFactorStatus, WebAuthnCredentialSummary } from '@/types'
import { isWebAuthnSupported } from '@/utils/webauthn'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import OTPInput from 'react-otp-input'
import { QRCodeSVG } from 'qrcode.react'

const serverError = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback

/** A step-up re-auth: the account password must be re-entered before a sensitive factor change. */
const ReauthDialog = ({
  open,
  title,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  busy: boolean
  onCancel: () => void
  onConfirm: (password: string) => void
}) => {
  const [password, setPassword] = useState('')
  useEffect(() => {
    if (!open) setPassword('')
  }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        className="w-full max-w-sm rounded-2xl bg-navyBlue p-6 flex flex-col gap-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (password) onConfirm(password)
        }}
      >
        <h3 className="text-white font-bold text-lg">{title}</h3>
        <p className="text-white/70 text-sm">Confirm your password to continue.</p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full p-3 rounded-full text-sm text-navyBlue focus:outline-none"
        />
        <div className="flex justify-end gap-x-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-white/70 hover:text-white text-sm">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !password}
            className={`${busy || !password ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'} rounded-full px-5 py-2 text-sm`}
          >
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Passkey enrolment is switched off for now: partner sign-in cannot perform passkeys (embedded
 * iframes / WebViews), so offering them as a strong factor strands those users. The section and
 * its plumbing are kept intact behind this flag to be re-enabled later. An account that already
 * holds a passkey still sees the list so it can remove it; it just cannot add another.
 */
const PASSKEY_ENROLMENT_ENABLED = false

type Reauth = { kind: 'totp' | 'totpDisable' | 'passkey' | 'delete' | 'recovery'; credentialId?: string }

/**
 * The one-time recovery-code reveal — shown ONCE after generation and never again, so the whole
 * point is to make sure the user actually saves them. Copy + download are one tap, and "Done" is
 * gated behind an explicit "I've saved these" confirmation so the codes can't be dismissed by
 * accident and lost.
 */
const RecoveryCodesModal = ({ codes, onClose }: { codes: string[]; onClose: () => void }) => {
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)
  const asText = codes.join('\n')

  const copyAll = () => {
    navigator.clipboard?.writeText(asText)
    toast.success(t('common.copied', 'Copied'))
  }
  const download = () => {
    const blob = new Blob([`DUPR recovery codes\n\n${asText}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'dupr-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-2xl bg-navyBlue p-6 flex flex-col gap-y-4">
        <h3 className="text-white font-bold text-lg">
          {t('twoFactor.saveRecoveryCodes', 'Save your recovery codes')}
        </h3>
        <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-3">
          <p className="text-amber-100 text-sm">
            {t(
              'twoFactor.recoveryCodesWarning',
              "Store these somewhere safe. Each code works once, and you won't be able to see them again."
            )}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-white/5 p-4" data-testid="recovery-codes">
          {codes.map((c) => (
            <span key={c} className="font-mono text-white text-center tracking-widest text-sm">
              {c}
            </span>
          ))}
        </div>
        <div className="flex gap-x-3">
          <button
            type="button"
            onClick={copyAll}
            className="flex-1 bg-white/10 text-white rounded-full py-2 text-sm hover:bg-white/20"
          >
            {t('common.copy', 'Copy')}
          </button>
          <button
            type="button"
            onClick={download}
            className="flex-1 bg-white/10 text-white rounded-full py-2 text-sm hover:bg-white/20"
          >
            {t('common.download', 'Download')}
          </button>
        </div>
        <label className="flex items-center gap-x-2 text-white/80 text-sm cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t('twoFactor.recoveryCodesSavedConfirm', "I've saved my recovery codes")}
        </label>
        <button
          type="button"
          onClick={onClose}
          disabled={!confirmed}
          className="bg-azul text-white rounded-full py-2.5 text-sm hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common.done', 'Done')}
        </button>
      </div>
    </div>
  )
}

/**
 * `variant`:
 * - `settings` (default): the MFA sub-section of the Password & Security settings page. Rendered
 *   bare — the section page (`pass-security.tsx`) owns the dashboard shell, the heading and the
 *   sub-navigation, and mounts this as a nested route.
 * - `onboarding`: the forced strong-MFA enrolment gate — rendered full-screen by App with NO shell
 *   around it (so none of the shell's background queries fire while the account is not yet fully
 *   authenticated). Once a strong factor is enrolled it shows a Continue action that calls
 *   [onEnrolled], which the gate uses to clear the requirement and enter the app.
 */
export const TwoFactor = ({
  variant = 'settings',
  onEnrolled,
}: {
  variant?: 'settings' | 'onboarding'
  onEnrolled?: () => void
} = {}) => {
  const { t } = useTranslation()

  const [status, setStatus] = useState<TwoFactorStatus | null>(null)
  const [passkeys, setPasskeys] = useState<WebAuthnCredentialSummary[]>([])
  const [reauth, setReauth] = useState<Reauth | null>(null)
  const [totpSetup, setTotpSetup] = useState<TotpSetupResult | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const setupTotp = useTotpSetup()
  const enableTotp = useTotpEnable()
  const disableTotp = useTotpDisable()
  const registerPasskey = useRegisterPasskey()
  const deletePasskey = useDeletePasskey()
  const generateRecovery = useGenerateRecoveryCodes()

  // A strong factor (authenticator or passkey) is the prerequisite for recovery codes, and is also
  // what turns off the emailed sign-in code — so it drives both the recovery section and the warning.
  const hasStrongFactor = !!status?.totpEnrolled || passkeys.length > 0
  const recoveryRemaining = status?.recoveryCodesRemaining ?? 0

  const refreshStatus = useCallback(() => {
    getTwoFactorStatus()
      .then(setStatus)
      .catch((e) => toast.error(serverError(e, 'Could not load your 2FA settings.')))
  }, [])
  const refreshPasskeys = useCallback(() => {
    listPasskeys()
      .then(setPasskeys)
      .catch(() => setPasskeys([]))
  }, [])

  // Mount-time reads happen ONLY in the settings variant, which is reachable solely from a fully
  // authenticated session. The onboarding gate is, by definition, a session that is not: the server
  // just said strong-MFA setup is owed, i.e. no strong factor is enrolled, so there is nothing to
  // read — and any automatic call from this state is exactly what must not happen. Every read here
  // is triggered by the user's own enrolment actions (the mutations refresh on success).
  useEffect(() => {
    if (variant !== 'settings') return
    refreshStatus()
    refreshPasskeys()
  }, [variant, refreshStatus, refreshPasskeys])

  const onReauth = (password: string) => {
    if (!reauth) return
    if (reauth.kind === 'totp') {
      setupTotp.mutate(
        { password },
        {
          onSuccess: (res) => {
            setTotpSetup(res)
            setTotpCode('')
            setReauth(null)
          },
          onError: (e) => toast.error(serverError(e, 'That password was not accepted.')),
        }
      )
    } else if (reauth.kind === 'totpDisable') {
      disableTotp.mutate(
        { password },
        {
          onSuccess: () => {
            toast.success('Authenticator removed.')
            setReauth(null)
            refreshStatus()
          },
          onError: (e) => toast.error(serverError(e, 'That password was not accepted.')),
        }
      )
    } else if (reauth.kind === 'passkey') {
      registerPasskey.mutate(
        { password },
        {
          onSuccess: () => {
            setReauth(null)
            refreshPasskeys()
            refreshStatus()
            toast.success('Passkey added.')
          },
          onError: (e) => toast.error(serverError(e, 'Could not add the passkey.')),
        }
      )
    } else if (reauth.kind === 'delete' && reauth.credentialId) {
      deletePasskey.mutate(
        { credentialId: reauth.credentialId, password },
        {
          onSuccess: () => {
            setReauth(null)
            refreshPasskeys()
            toast.success('Passkey removed.')
          },
          onError: (e) => toast.error(serverError(e, 'Could not remove the passkey.')),
        }
      )
    } else if (reauth.kind === 'recovery') {
      generateRecovery.mutate(
        { password },
        {
          onSuccess: (codes) => {
            setReauth(null)
            setRecoveryCodes(codes)
            refreshStatus()
          },
          onError: (e) => toast.error(serverError(e, 'Could not generate recovery codes.')),
        }
      )
    }
  }

  const onEnableTotp = () => {
    if (totpCode.length !== 6) return
    enableTotp.mutate(
      { code: totpCode },
      {
        onSuccess: () => {
          toast.success('Authenticator enabled.')
          setTotpSetup(null)
          setTotpCode('')
          refreshStatus()
        },
        onError: (e) => {
          // Clear the boxes so the next attempt is typed from a freshly displayed code.
          setTotpCode('')
          toast.error(serverError(e, 'That code was not accepted.'))
        },
      }
    )
  }

  const otpInputStyle = 'flex-1 border rounded-xl aspect-square max-w-[56px] text-center text-navyBlue'

  const body = (
    <>
        <div className="flex flex-col items-center w-full">
          <div className="flex flex-col w-full lg:w-[520px] gap-y-8 mb-20 lg:mb-8">
            {variant === 'onboarding' && (
              <div className="flex flex-col gap-y-2">
                <h1 className="text-2xl md:text-[32px] md:leading-10 font-bold text-white">
                  {t('twoFactor.onboardingTitle', 'Secure your account')}
                </h1>
                <p className="text-white/70 text-sm">
                  {t(
                    'twoFactor.onboardingSubtitle',
                    'Your account requires an authenticator app or passkey before you can continue. Set one up below.'
                  )}
                </p>
              </div>
            )}

            {/* Once a strong factor is on, login is strong-only (no emailed code) — warn about lockout. */}
            {hasStrongFactor && (
              <div className="flex items-start gap-x-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
                <span aria-hidden className="text-amber-300 text-lg leading-none">
                  ⚠
                </span>
                <div className="flex flex-col gap-y-1">
                  <p className="text-amber-100 text-sm">
                    {t(
                      'twoFactor.emailCodesOff',
                      "Email sign-in codes are turned off for your account. If you lose your authenticator or passkey, you'll need a recovery code to get back in."
                    )}
                  </p>
                  {recoveryRemaining === 0 && (
                    <button
                      onClick={() => setReauth({ kind: 'recovery' })}
                      className="self-start text-amber-200 text-sm font-semibold hover:underline"
                    >
                      {t('twoFactor.generateNow', 'Generate recovery codes now')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ---------- Authenticator app (TOTP) ---------- */}
            <section className="flex flex-col gap-y-3 rounded-2xl bg-white/5 p-5">
              <h2 className="text-lg font-bold text-white">{t('twoFactor.authenticator', 'Authenticator app')}</h2>
              <p className="text-white/70 text-sm">
                {t(
                  'twoFactor.authenticatorDesc',
                  'Use an app like Google Authenticator or 1Password to generate sign-in codes.'
                )}
              </p>

              {status?.totpEnrolled && (
                <div className="flex items-center justify-between">
                  <span className="text-green-400 text-sm font-semibold">● Enabled</span>
                  <button
                    onClick={() => setReauth({ kind: 'totpDisable' })}
                    className="text-warn text-sm hover:underline"
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                </div>
              )}

              {!status?.totpEnrolled && !totpSetup && (
                <button
                  onClick={() => setReauth({ kind: 'totp' })}
                  className="self-start bg-azul text-white rounded-full px-5 py-2 text-sm hover:bg-primary"
                >
                  {t('twoFactor.setUpAuthenticator', 'Set up authenticator')}
                </button>
              )}

              {!status?.totpEnrolled && totpSetup && (
                <div className="flex flex-col items-center gap-y-4">
                  <div className="rounded-xl bg-white p-3">
                    <QRCodeSVG value={totpSetup.otpauthUri} size={180} />
                  </div>
                  <p className="text-white/70 text-xs text-center">
                    {t('twoFactor.scanQr', 'Scan the QR code, or enter this key manually:')}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(totpSetup.secret)
                      toast.success(t('common.copied', 'Copied'))
                    }}
                    className="font-mono text-sm text-white bg-white/10 rounded-lg px-3 py-2 break-all"
                  >
                    {totpSetup.secret}
                  </button>
                  {/* Every setup mints a new secret, so a DUPR entry left in the app by an earlier
                      attempt now produces codes that never match. */}
                  <p className="text-amber-100 text-xs text-center">
                    {t(
                      'twoFactor.replacesOldEntry',
                      'This replaces any earlier DUPR entry in your authenticator app. Delete older DUPR entries so you enter the code from this one.'
                    )}
                  </p>
                  <p className="text-white/70 text-sm">{t('twoFactor.enterCode', 'Enter the 6-digit code to confirm.')}</p>
                  <OTPInput
                    value={totpCode}
                    onChange={(v) => setTotpCode(v.replace(/\D/g, '').slice(0, 6))}
                    numInputs={6}
                    inputStyle={otpInputStyle}
                    containerStyle="justify-between gap-x-2 w-full"
                    renderInput={(props) => <input {...props} inputMode="numeric" />}
                  />
                  <div className="flex justify-end gap-x-3 w-full">
                    <button
                      onClick={() => {
                        setTotpSetup(null)
                        setTotpCode('')
                      }}
                      className="text-white/70 text-sm hover:text-white"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                      onClick={onEnableTotp}
                      disabled={enableTotp.isLoading || totpCode.length !== 6}
                      className="bg-azul text-white rounded-full px-5 py-2 text-sm hover:bg-primary disabled:opacity-50"
                    >
                      {t('twoFactor.enable', 'Enable')}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* ---------- Passkeys (WebAuthn) ---------- */}
            <section className="flex flex-col gap-y-3 rounded-2xl bg-white/5 p-5">
              <h2 className="text-lg font-bold text-white">{t('twoFactor.passkeys', 'Passkeys')}</h2>
              <p className="text-white/70 text-sm">
                {t('twoFactor.passkeysDesc', 'Sign in with your device’s fingerprint, face, or screen lock.')}
              </p>

              {/* The partner sign-in surface is embedded in iframes / native WebViews where the
                  credential API is refused or missing, so it does not perform passkeys at all. */}
              <div className="flex items-start gap-x-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
                <span aria-hidden className="text-amber-300 text-lg leading-none">
                  ⚠
                </span>
                <div className="flex flex-col gap-y-1">
                  <p className="text-amber-100 text-sm">
                    {t(
                      'twoFactor.passkeysNoPartnerSso',
                      "Passkeys are not currently available when you sign in to DUPR through a partner's app or website. If you use partner sign-in, also set up an authenticator app."
                    )}
                  </p>
                  {!status?.totpEnrolled && (
                    <button
                      onClick={() => setReauth({ kind: 'totp' })}
                      className="self-start text-amber-200 text-sm font-semibold hover:underline"
                    >
                      {t('twoFactor.setUpAuthenticator', 'Set up authenticator')}
                    </button>
                  )}
                </div>
              </div>

              {passkeys.length > 0 && (
                <ul className="flex flex-col gap-y-2">
                  {passkeys.map((pk) => (
                    <li key={pk.credentialId} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-white text-sm font-semibold">{pk.label || 'Passkey'}</span>
                        {pk.lastUsed && (
                          <span className="text-white/50 text-xs">
                            {t('twoFactor.lastUsed', 'Last used')} {new Date(pk.lastUsed).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setReauth({ kind: 'delete', credentialId: pk.credentialId })}
                        className="text-warn text-sm hover:underline"
                      >
                        {t('common.remove', 'Remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {PASSKEY_ENROLMENT_ENABLED &&
                (isWebAuthnSupported() ? (
                  <button
                    onClick={() => setReauth({ kind: 'passkey' })}
                    disabled={registerPasskey.isLoading}
                    className="self-start bg-azul text-white rounded-full px-5 py-2 text-sm hover:bg-primary disabled:opacity-50"
                  >
                    {t('twoFactor.addPasskey', 'Add a passkey')}
                  </button>
                ) : (
                  <p className="text-white/50 text-sm">
                    {t('twoFactor.passkeysUnsupported', 'This browser does not support passkeys.')}
                  </p>
                ))}
            </section>

            {/* ---------- Recovery codes ---------- */}
            <section className="flex flex-col gap-y-3 rounded-2xl bg-white/5 p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">{t('twoFactor.recoveryCodes', 'Recovery codes')}</h2>
                {hasStrongFactor && recoveryRemaining > 0 && (
                  <span className="text-white/60 text-sm" data-testid="recovery-remaining">
                    {t('twoFactor.codesLeft', '{{n}} of 10 left', { n: recoveryRemaining })}
                  </span>
                )}
              </div>
              <p className="text-white/70 text-sm">
                {t(
                  'twoFactor.recoveryCodesDesc',
                  'One-time backup codes to sign in if you lose your authenticator or passkey. Each code works once.'
                )}
              </p>

              {!hasStrongFactor ? (
                <p className="text-white/50 text-sm">
                  {t(
                    'twoFactor.recoveryNeedsStrong',
                    'Add an authenticator app or a passkey first to generate recovery codes.'
                  )}
                </p>
              ) : recoveryRemaining === 0 ? (
                <button
                  onClick={() => setReauth({ kind: 'recovery' })}
                  disabled={generateRecovery.isLoading}
                  className="self-start bg-azul text-white rounded-full px-5 py-2 text-sm hover:bg-primary disabled:opacity-50"
                >
                  {t('twoFactor.generateRecovery', 'Generate recovery codes')}
                </button>
              ) : (
                <div className="flex flex-col gap-y-2">
                  {recoveryRemaining <= 3 && (
                    <p className="text-warn text-sm">
                      {t('twoFactor.recoveryLow', 'Only a few codes left — generate a new set soon.')}
                    </p>
                  )}
                  <button
                    onClick={() => setReauth({ kind: 'recovery' })}
                    disabled={generateRecovery.isLoading}
                    className="self-start text-azul text-sm hover:underline disabled:opacity-50"
                  >
                    {t('twoFactor.regenerateRecovery', 'Generate new codes')}
                  </button>
                  <p className="text-white/40 text-xs">
                    {t('twoFactor.regenerateNote', 'Generating a new set replaces your existing codes.')}
                  </p>
                </div>
              )}
            </section>

            {variant === 'onboarding' && hasStrongFactor && (
              <button
                onClick={() => onEnrolled?.()}
                className="mt-2 bg-azul text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-primary"
              >
                {t('twoFactor.continueToDupr', 'Continue to DUPR')}
              </button>
            )}
            {/* The gate reads nothing on mount, so a factor enrolled elsewhere (another device, or
                support) is not visible here. This lets the user say so: it clears the flag and the
                app's profile fetch confirms — or, if setup is still owed, re-raises the gate. */}
            {variant === 'onboarding' && !hasStrongFactor && (
              <button
                onClick={() => onEnrolled?.()}
                className="mt-2 self-start text-sm text-white/60 hover:text-white underline underline-offset-4"
              >
                {t('twoFactor.alreadySetUp', 'I already set this up on another device')}
              </button>
            )}
          </div>
        </div>
    </>
  )

  return (
    <>
      {variant === 'onboarding' ? (
        // A fixed-viewport box that scrolls internally: the app locks document scrolling, and a
        // min-height box would just grow past the bottom with no way to reach the sections (and
        // the Continue action) below the fold.
        <div className="h-screen w-full bg-navyBlue overflow-y-auto py-10 px-4">{body}</div>
      ) : (
        body
      )}

      <ReauthDialog
        open={!!reauth}
        title={
          reauth?.kind === 'delete'
            ? t('twoFactor.removePasskey', 'Remove passkey')
            : reauth?.kind === 'totpDisable'
              ? t('twoFactor.removeAuthenticator', 'Remove authenticator')
              : reauth?.kind === 'passkey'
                ? t('twoFactor.addPasskey', 'Add a passkey')
                : reauth?.kind === 'recovery'
                  ? t('twoFactor.generateRecovery', 'Generate recovery codes')
                  : t('twoFactor.setUpAuthenticator', 'Set up authenticator')
        }
        busy={
          setupTotp.isLoading ||
          disableTotp.isLoading ||
          registerPasskey.isLoading ||
          deletePasskey.isLoading ||
          generateRecovery.isLoading
        }
        onCancel={() => setReauth(null)}
        onConfirm={onReauth}
      />

      {recoveryCodes && (
        <RecoveryCodesModal
          codes={recoveryCodes}
          onClose={() => {
            setRecoveryCodes(null)
            refreshStatus()
          }}
        />
      )}
    </>
  )
}

export default TwoFactor
